import { useAnalytics } from '@repo/analytics/analytics-provider';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import {
    useCreateCustomMarkerMutation,
    useDeleteCustomMarkerMutation,
    useUpdateCustomMarkerMutation,
} from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectIsAuthenticated } from '@repo/store/maps/slices/auth';
import {
    addCustomMarker,
    selectCustomPinEditingState,
    selectExternalMapId,
    selectIsAddingCustomLocation,
    setCustomPinEditingState,
    setIsAddingCustomLocation,
} from '@repo/store/maps/slices/mapData';
import { DEFAULT_PIN_COLOR } from '@repo/store/maps/utils/mapData';
import { useCallback, useEffect, useRef } from 'react';
import { useMapContext } from '../map-context';
import { PIN_INTERACTION_LAYER_IDS } from './constants';
import { useMapCursor } from './useMapCursor';
import { useMapPinOperations } from './useMapPinOperations';

export function useCustomPinOperations() {
    const dispatch = useAppDispatch();
    const { map } = useMapContext();
    const { t } = useT(['game_map']);
    const externalId = useAppSelector(selectExternalMapId);
    const { selectMarkerInMap, clearSelectedMarkerInMap } = useMapPinOperations();
    const { setCursor } = useMapCursor();
    const analytics = useAnalytics();
    const hostType = useHostType();
    const isAuthenticated = useAppSelector(selectIsAuthenticated);

    // Use global state instead of local state
    const isAddingCustomPin = useAppSelector(selectIsAddingCustomLocation);
    const {
        isEditing: isEditingCustomPin,
        pinId: editingPinId,
        isNewlyCreated: isEditingNewlyCreatedPin,
        pinData,
    } = useAppSelector(selectCustomPinEditingState);

    const [createCustomMarker, { isLoading: isCreatingMarker }] = useCreateCustomMarkerMutation();
    const [updateCustomMarker, { isLoading: isUpdatingMarker }] = useUpdateCustomMarkerMutation();
    const [deleteCustomMarker, { isLoading: isDeletingMarker }] = useDeleteCustomMarkerMutation();

    // Store the map click handler reference
    const mapClickHandlerRef = useRef<((e: maplibregl.MapMouseEvent) => void) | null>(null);
    const escapeHandlerRef = useRef<((e: KeyboardEvent) => void) | null>(null);
    const clickOutsideHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
    const isCreatingPinRef = useRef(false);

    // Cleanup function to remove event listeners
    const cleanupEventListeners = useCallback(() => {
        if (map && mapClickHandlerRef.current) {
            map.off('click', mapClickHandlerRef.current);
            mapClickHandlerRef.current = null;
        }
        if (escapeHandlerRef.current) {
            document.removeEventListener('keydown', escapeHandlerRef.current);
            escapeHandlerRef.current = null;
        }
        if (clickOutsideHandlerRef.current) {
            document.removeEventListener('click', clickOutsideHandlerRef.current);
            clickOutsideHandlerRef.current = null;
        }

        // Return to grab cursor when cleaning up
        if (map) {
            map.getCanvas().style.cursor = 'grab';
        }
    }, [map]);

    // Cleanup when adding state changes
    useEffect(() => {
        if (!isAddingCustomPin) {
            cleanupEventListeners();
        }
    }, [isAddingCustomPin, cleanupEventListeners]);

    // Cleanup on unmount
    useEffect(() => {
        return cleanupEventListeners;
    }, [cleanupEventListeners]);

    const startAddingCustomPin = useCallback(
        async (
            pinData?: { title: string; description: string; color: string; coordinates: [number, number] },
            targetMap?: maplibregl.Map,
        ) => {
            const mapTarget = targetMap || map;

            if (!mapTarget || !externalId || !isAuthenticated) {
                return;
            }

            if (pinData) {
                try {
                    const result = await createCustomMarker({
                        externalId,
                        lng: pinData.coordinates[0],
                        lat: pinData.coordinates[1],
                        title: pinData.title,
                        description: pinData.description,
                        color: pinData.color,
                    }).unwrap();

                    if (result) {
                        dispatch(addCustomMarker(result));
                        selectMarkerInMap(result.properties.externalId, {
                            feature: result,
                            isNewlyCreated: true,
                        });
                        dispatch(
                            setCustomPinEditingState({
                                isEditing: true,
                                pinId: result.properties.externalId,
                                isNewlyCreated: true,
                                pinData: pinData,
                            }),
                        );
                    }
                } catch {
                    toast({
                        title: t($ => $.add_custom_marker_error, { ns: 'game_map' }),
                        variant: 'error',
                    });
                } finally {
                    dispatch(setIsAddingCustomLocation(false));
                    cleanupEventListeners();
                }

                return;
            }

            dispatch(setIsAddingCustomLocation(true));

            setCursor('crosshair');

            // Create the map click handler
            const handleMapClick = async (e: maplibregl.MapMouseEvent) => {
                if (isCreatingPinRef.current) return;

                // Check if we clicked on a marker - if so, don't create a custom pin
                let features: maplibregl.MapGeoJSONFeature[] = [];
                try {
                    features = mapTarget.queryRenderedFeatures(e.point, {
                        layers: [...PIN_INTERACTION_LAYER_IDS],
                    });
                } catch {
                    // maplibre dictionary_coder crashes when a tile's symbol index is empty
                }
                if (features.length > 0) {
                    return; // Let the existing marker click handler deal with this
                }

                const { lng, lat } = e.lngLat;
                isCreatingPinRef.current = true;

                try {
                    const result = await createCustomMarker({
                        externalId,
                        lng,
                        lat,
                        title: 'Custom Pin',
                        description: '',
                        color: DEFAULT_PIN_COLOR,
                    }).unwrap();

                    // After creating the marker, select it and open editing mode
                    if (result) {
                        dispatch(addCustomMarker(result));
                        selectMarkerInMap(result.properties.externalId, {
                            feature: result,
                            isNewlyCreated: true,
                        });
                        dispatch(
                            setCustomPinEditingState({
                                isEditing: true,
                                pinId: result.properties.externalId,
                                isNewlyCreated: true,
                                pinData: {
                                    title: 'Custom Pin',
                                    description: '',
                                    color: DEFAULT_PIN_COLOR,
                                    coordinates: [lng, lat],
                                },
                            }),
                        );
                    }
                } catch {
                    toast({
                        title: t($ => $.add_custom_marker_error, { ns: 'game_map' }),
                        variant: 'error',
                    });
                } finally {
                    isCreatingPinRef.current = false;
                    dispatch(setIsAddingCustomLocation(false));
                    cleanupEventListeners();
                }
            };

            // Create escape key handler
            const handleEscape = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    setCursor('grab');
                    dispatch(setIsAddingCustomLocation(false));
                    cleanupEventListeners();
                }
            };

            // Create click outside handler
            const handleClickOutside = (e: MouseEvent) => {
                // Check if the click is on the map canvas or its children
                const target = e.target as Element;
                const mapContainer = mapTarget.getContainer();

                // If click is not on the map container or its children, cancel adding mode
                if (!mapContainer.contains(target)) {
                    setCursor('grab');
                    dispatch(setIsAddingCustomLocation(false));
                    cleanupEventListeners();
                }
            };

            // Store the handler references
            mapClickHandlerRef.current = handleMapClick;
            escapeHandlerRef.current = handleEscape;
            clickOutsideHandlerRef.current = handleClickOutside;

            // Add event listeners
            mapTarget.on('click', handleMapClick);
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('click', handleClickOutside);
        },
        [
            map,
            externalId,
            dispatch,
            setCursor,
            createCustomMarker,
            selectMarkerInMap,
            cleanupEventListeners,
            isAuthenticated,
            t,
        ],
    );

    const cancelAddingCustomPin = useCallback(() => {
        if (!map) return;

        setCursor('grab');
        dispatch(setIsAddingCustomLocation(false));
        cleanupEventListeners();
    }, [map, dispatch, setCursor, cleanupEventListeners]);

    const handleSaveCustomPin = useCallback(
        async (data: { title: string; description: string; color: string }) => {
            if (!editingPinId || !externalId) return;

            if (isEditingNewlyCreatedPin) {
                if (pinData?.coordinates) {
                    try {
                        await updateCustomMarker({
                            markerId: editingPinId,
                            title: data.title,
                            description: data.description,
                            color: data.color,
                            coordinates: pinData.coordinates,
                            externalId,
                        }).unwrap();

                        analytics?.event('game_map_custom_marker_created', {
                            mapId: externalId,
                            markerId: editingPinId,
                            platform: hostType,
                        });
                    } catch (error) {
                        analytics?.event('game_map_custom_marker_create_failed', {
                            mapId: externalId,
                            platform: hostType,
                        });
                        throw error;
                    }
                } else {
                    if (!map) return;

                    const selectedFeature = map
                        .querySourceFeatures('markers')
                        .find(feature => feature.properties?.externalId === editingPinId);

                    if (!selectedFeature) return;

                    let coordinates: [number, number] = [0, 0];
                    if (selectedFeature.geometry.type === 'Point') {
                        const coords = selectedFeature.geometry.coordinates;
                        if (
                            Array.isArray(coords) &&
                            coords.length >= 2 &&
                            coords[0] !== undefined &&
                            coords[1] !== undefined
                        ) {
                            coordinates = [coords[0], coords[1]];
                        }
                    }

                    try {
                        await updateCustomMarker({
                            markerId: editingPinId,
                            title: data.title,
                            description: data.description,
                            color: data.color,
                            coordinates,
                            externalId,
                        }).unwrap();

                        analytics?.event('game_map_custom_marker_updated', {
                            mapId: externalId,
                            markerId: editingPinId,
                            platform: hostType,
                        });
                    } catch (error) {
                        analytics?.event('game_map_custom_marker_update_failed', {
                            mapId: externalId,
                            markerId: editingPinId,
                            platform: hostType,
                        });
                        throw error;
                    }
                }
            } else {
                // For existing pins, update them
                if (!map) return;

                // Get the current marker coordinates
                const selectedFeature = map
                    .querySourceFeatures('markers')
                    .find(feature => feature.properties?.externalId === editingPinId);

                if (!selectedFeature) return;

                let coordinates: [number, number] = [0, 0];
                if (selectedFeature.geometry.type === 'Point') {
                    const coords = selectedFeature.geometry.coordinates;
                    if (
                        Array.isArray(coords) &&
                        coords.length >= 2 &&
                        coords[0] !== undefined &&
                        coords[1] !== undefined
                    ) {
                        coordinates = [coords[0], coords[1]];
                    }
                }

                try {
                    await updateCustomMarker({
                        markerId: editingPinId,
                        title: data.title,
                        description: data.description,
                        color: data.color,
                        coordinates,
                        externalId,
                    }).unwrap();

                    analytics?.event('game_map_custom_marker_updated', {
                        mapId: externalId,
                        markerId: editingPinId,
                        platform: hostType,
                    });
                } catch (error) {
                    analytics?.event('game_map_custom_marker_update_failed', {
                        mapId: externalId,
                        markerId: editingPinId,
                        platform: hostType,
                    });
                    throw error;
                }
            }

            dispatch(setCustomPinEditingState({ isEditing: false, pinId: null, isNewlyCreated: false, pinData: null }));
        },
        [
            editingPinId,
            map,
            updateCustomMarker,
            externalId,
            dispatch,
            isEditingNewlyCreatedPin,
            pinData,
            analytics,
            hostType,
        ],
    );

    const handleCancelCustomPin = useCallback(async () => {
        if (!editingPinId || !externalId) return;

        if (isEditingNewlyCreatedPin) {
            try {
                await deleteCustomMarker({ markerId: editingPinId, externalId }).unwrap();
            } catch (error) {
                console.error('Failed to delete newly created pin:', error);
            }
        }

        clearSelectedMarkerInMap();
        dispatch(setCustomPinEditingState({ isEditing: false, pinId: null, isNewlyCreated: false, pinData: null }));
    }, [editingPinId, clearSelectedMarkerInMap, externalId, dispatch, isEditingNewlyCreatedPin, deleteCustomMarker]);

    const handleDeleteCustomPin = useCallback(
        async (pinId: string) => {
            if (!externalId) return;

            try {
                await deleteCustomMarker({ markerId: pinId, externalId }).unwrap();

                analytics?.event('game_map_custom_marker_deleted', {
                    mapId: externalId,
                    markerId: pinId,
                    platform: hostType,
                });

                if (editingPinId === pinId) {
                    clearSelectedMarkerInMap();
                    dispatch(
                        setCustomPinEditingState({
                            isEditing: false,
                            pinId: null,
                            isNewlyCreated: false,
                            pinData: null,
                        }),
                    );
                }
            } catch (error) {
                analytics?.event('game_map_custom_marker_delete_failed', {
                    mapId: externalId,
                    markerId: pinId,
                    platform: hostType,
                });
                throw error;
            }
        },
        [externalId, deleteCustomMarker, analytics, hostType, editingPinId, clearSelectedMarkerInMap, dispatch],
    );

    const handleEditCustomPin = useCallback(
        (pinId: string) => {
            dispatch(setCustomPinEditingState({ isEditing: true, pinId, isNewlyCreated: false, pinData: null }));
        },
        [dispatch],
    );

    return {
        // State
        isAddingCustomPin,
        isEditingCustomPin,
        editingPinId,
        isEditingNewlyCreatedPin,

        // Loading states
        isCreatingMarker,
        isUpdatingMarker,
        isDeletingMarker,

        // Actions
        startAddingCustomPin,
        cancelAddingCustomPin,
        handleSaveCustomPin,
        handleCancelCustomPin,
        handleDeleteCustomPin,
        handleEditCustomPin,
    };
}
